import {
    MainButton,
    SelectInputField,
    Typography,
} from '@eventr/common-storybook'
import { Box } from '@mui/material'
import { FC } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { THotelRoom } from '../../../state/bookingState/booking.interfaces'
import { useEphemeralStore } from '../../../state/useEphemeralStore'
import { NumberSelector } from '../comps/numberSelector'

type TRoomDrawerProps = {
    close: () => void
}

export const RoomDrawer: FC<TRoomDrawerProps> = ({ close }) => {
    const { rooms, setRooms } = useEphemeralStore(
        useShallow((s) => ({
            rooms: s.rooms,
            setRooms: s.setRooms,
        }))
    )

    function changeAdultsInRoomBy(roomIndex: number, changeBy: number) {
        const tmpRooms = structuredClone(rooms)
        tmpRooms[roomIndex] = {
            adults: tmpRooms[roomIndex].adults + changeBy,
            children: tmpRooms[roomIndex].children,
        }
        setRooms([...tmpRooms])
    }

    function removeRoom(roomIndex: number) {
        const tmpRooms = structuredClone(rooms)
        tmpRooms.splice(roomIndex, 1)
        setRooms([...tmpRooms])
    }

    function addChild(roomIndex: number) {
        const tmpRooms = structuredClone(rooms)
        tmpRooms[roomIndex].children.push(13)
        setRooms([...tmpRooms])
    }

    function removeChild(roomIndex: number) {
        const tmpRooms = structuredClone(rooms)
        tmpRooms[roomIndex].children.pop()
        setRooms([...tmpRooms])
    }

    function changeAge(roomIndex: number, childIndex: number, newAge: number) {
        const tmpRooms = structuredClone(rooms)
        tmpRooms[roomIndex].children[childIndex] = newAge
        setRooms([...tmpRooms])
    }

    return (
        <Box display={'grid'} width={'100%'}>
            <Box display={'grid'} justifyItems={'center'}>
                <Typography>{i18n.t('booking.rooms')}</Typography>
            </Box>
            <Box height={'16px'} />

            {rooms.map((room, roomIndex) => {
                return (
                    <RoomComp
                        key={'room' + roomIndex}
                        roomIndex={roomIndex}
                        room={room}
                        changeAdultsInRoomBy={changeAdultsInRoomBy}
                        changeAge={changeAge}
                        addChild={addChild}
                        removeChild={removeChild}
                        roomsLength={rooms.length}
                        removeRoom={removeRoom}
                    />
                )
            })}
            <Box display={'grid'} justifyItems={'end'}>
                <MainButton
                    text={i18n.t('booking.addRoom')}
                    onClick={() => {
                        setRooms([...rooms, { adults: 1, children: [] }])
                    }}
                    variant="secondary"
                    size="small"
                />
            </Box>
            <Box height={'16px'} />
            <MainButton text={i18n.t('general.done')} onClick={close} />
            <Box height={'32px'} />
        </Box>
    )
}

type TRoomCompProps = {
    roomIndex: number
    room: THotelRoom
    changeAdultsInRoomBy: (roomIndex: number, changeBy: number) => void
    addChild: (roomIndex: number) => void
    removeChild: (roomIndex: number) => void
    changeAge: (roomIndex: number, childIndex: number, newAge: number) => void
    roomsLength: number
    removeRoom: (roomIndex: number) => void
}

const RoomComp: FC<TRoomCompProps> = ({
    roomIndex,
    room,
    changeAdultsInRoomBy,
    addChild,
    removeChild,
    changeAge,
    roomsLength,
    removeRoom,
}) => {
    return (
        <Box padding={'0 0 16px 0'}>
            <Typography variant="bodyBold">
                {i18n.t('booking.room')} {roomIndex + 1}
            </Typography>
            <Box padding={'0 0 0 8px'}>
                <Box display={'grid'} gridTemplateColumns={'1fr auto'}>
                    <Typography>
                        {room.adults > 1
                            ? i18n.t('booking.adults')
                            : i18n.t('booking.adult')}
                    </Typography>
                    <NumberSelector
                        count={room.adults}
                        onAdd={() => {
                            changeAdultsInRoomBy(roomIndex, 1)
                        }}
                        onSubtract={() => {
                            changeAdultsInRoomBy(roomIndex, -1)
                        }}
                        range={[1, 9]}
                    />
                </Box>
                <Box display={'grid'} gridTemplateColumns={'1fr auto'}>
                    <Typography>{i18n.t('booking.child')}</Typography>
                    <NumberSelector
                        count={room.children.length}
                        onAdd={() => {
                            addChild(roomIndex)
                        }}
                        onSubtract={() => {
                            removeChild(roomIndex)
                        }}
                        range={[0, 9]}
                    />
                </Box>
                <Box padding={'0 0 0 8px'}>
                    {room.children.map((childAge, childIndex) => {
                        return (
                            <ChildComp
                                key={'child' + childIndex}
                                childIndex={childIndex}
                                age={childAge}
                                roomIndex={roomIndex}
                                changeAge={changeAge}
                            />
                        )
                    })}
                </Box>
            </Box>

            <Box height={'16px'} />
            {roomsLength > 1 && (
                <Box display={'grid'} justifyItems={'end'}>
                    <MainButton
                        variant="secondary"
                        text={i18n.t('booking.removeRoom')}
                        size="small"
                        onClick={() => {
                            removeRoom(roomIndex)
                        }}
                    />
                </Box>
            )}
        </Box>
    )
}

type ChildCompProps = {
    childIndex: number
    age: number
    roomIndex: number
    changeAge: (roomIndex: number, childIndex: number, newAge: number) => void
}

const ChildComp: FC<ChildCompProps> = ({
    childIndex,
    age,
    roomIndex,
    changeAge,
}) => {
    return (
        <Box padding={'8px 0 0 0'}>
            <SelectInputField
                fieldLabel={i18n.t('booking.childAge', {
                    num: childIndex + 1,
                })}
                defaultValue={age}
                data={Array.from(Array(18).keys()).map((num: number) => {
                    return {
                        displayName: String(num),
                        id: num,
                    }
                })}
                onChange={(event) => {
                    if (
                        event.target.value &&
                        typeof event.target.value === 'number'
                    ) {
                        changeAge(roomIndex, childIndex, event.target.value)
                    }
                }}
            />
        </Box>
    )
}
